import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { divIcon } from 'leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import { Box, Button, Container, Divider, Typography, FormControl, Select, MenuItem } from '@mui/material';
import apiService from '../components/ReasuableFuncations/api';


class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }
    return this.props.children;
  }
}

const AfricaMaps = () => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);
  const [activeOrganization, setActiveOrganization] = useState(null);
  const [userData, setUserData] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');

  const regionGeoScopes = {
    COMESA: [
      "Burundi", "Comoros", "DRC", "Djibouti", "Egypt", "Eritrea", "Eswatini",
      "Ethiopia", "Kenya", "Libya", "Madagascar", "Malawi", "Mauritius",
      "Rwanda", "Seychelles", "Somalia", "South Sudan", "Sudan", "Zambia",
      "Zimbabwe", "Tanzania"
    ],
    EAC: ["Burundi", "Kenya", "Rwanda", "South Sudan", "Tanzania", "Uganda", "Congo", "Somalia"],
    IGAD: [
      "Djibouti", "Eritrea", "Ethiopia", "Kenya", "Somalia", "South Sudan",
      "Sudan", "Uganda"
    ],
    IOC: [
      "Comoros", "France (Réunion)", "Madagascar", "Mauritius", "Seychelles"
    ],
    SADC: [
      "Angola", "Botswana", "Comoros", "DRC", "Eswatini", "Lesotho",
      "Madagascar", "Malawi", "Mozambique", "Namibia", "Seychelles",
      "South Africa", "Tanzania", "Zambia", "Zimbabwe"
    ]
  };

  const findRegionsForState = (stateName) => {
    return Object.entries(regionGeoScopes)
      .filter(([region, states]) => states.includes(stateName))
      .map(([region]) => region);
  };

  const fetchProjects = async () => {
    const storedUserData = JSON.parse(localStorage.getItem('user'));
    setUserData(storedUserData); 
    const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = storedUserData;
    
    let url = `/projects`;

    try {
      let response = await apiService.get(`${url}`);
      
      if (response && response.statusText !== 'OK') {
        throw new Error('Failed to fetch projects');
      }

      const data = response.data;
      
      // Enhanced project filtering logic
      const filteredProjects = data.reduce((acc, project) => {
        const relevantProjects = [];

        // Add project's primary location
        if (project.location) {
          relevantProjects.push({
            ...project,
            location: project.location,
            displayRegions: [project.organization],
            isMainLocation: true
          });
        }

        // Process collaborating states and their regions
        if (project.collaboratingStates?.length > 0) {
          project.collaboratingStates.forEach(state => {
            if (!state.name) return;
            
            // Find all regions this state belongs to
            const stateRegions = findRegionsForState(state.name);
            
            // Check if any of the state's regions match project's collaborating regions
            const matchingRegions = stateRegions.filter(region => 
              project.collaboratingRegions?.includes(region)
            );

            if (matchingRegions.length > 0) {
              relevantProjects.push({
                ...project,
                location: {
                  name: state.name,
                  latitude: state.latitude,
                  longitude: state.longitude
                },
                displayRegions: matchingRegions,
                isCollaboratingState: true
              });
            }
          });
        }

        return [...acc, ...relevantProjects];
      }, []);

      setProjects(filteredProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to load projects.');
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const groupProjectsByLocation = (filteredProjects) => {
    const groupedProjects = {};

    filteredProjects.forEach((project) => {
      const { latitude, longitude } = project.location || {};
      if (!latitude || !longitude) return;
      
      const locationKey = `${latitude}-${longitude}`;

      if (!groupedProjects[locationKey]) {
        groupedProjects[locationKey] = { 
          projects: [], 
          latitude, 
          longitude,
          regions: new Set()
        };
      }
      
      groupedProjects[locationKey].projects.push(project);
      
      // Add regions information
      if (project.displayRegions) {
        project.displayRegions.forEach(region => 
          groupedProjects[locationKey].regions.add(region)
        );
      }
    });

    return groupedProjects;
  };

  const createCustomIcon = (projectsAtLocation) => {
    const isMobile = window.innerWidth <= 768;
    return divIcon({
      className: 'custom-icon',
      html: renderToStaticMarkup(
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          whiteSpace: 'nowrap', 
          width: isMobile ? '150px' : '200px', 
          height: '40px' 
        }}>
          <svg height="20" width="20" viewBox="0 0 24 24" style={{ marginRight: '4px' }}>
            <path fill="#DB4437" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
            <circle cx="12" cy="9" r="2.5" fill="white" />
          </svg>
          <span style={{
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: 'bold',
            color: '#333',
            textShadow: '1px 1px 1px white, -1px -1px 1px white, 1px -1px 1px white, -1px 1px 1px white',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            width: isMobile ? '120px' : '160px',
            display: 'inline-block',
          }}>
            {projectsAtLocation.projects.length > 1 ? `${projectsAtLocation.projects.length} projects` : projectsAtLocation.projects[0].projectName}
          </span>
        </div>
      ),
      iconSize: [isMobile ? 150 : 200, 40],
      iconAnchor: [isMobile ? 75 : 100, 20],
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const handleButtonClick = (organization) => {
    if (organization === 'ALL') {
      setActiveOrganization(null); 
    } else {
      setActiveOrganization(organization);
    }
  };

  // Modify handleProjectSelect to reset organization when project changes
  const handleProjectSelect = (event) => {
    const projectName = event.target.value;
    setSelectedProject(projectName);
    setActiveOrganization(null);
    
    if (projectName) {
      const project = projects.find(p => p.projectName === projectName);
      if (project) {
        const map = document.querySelector('.leaflet-container')?._leafletInstance;
        if (map) {
          map.setView([project.location.latitude, project.location.longitude], 6);
        }
      }
    }
  };

  // Add this new function to get unique organizations from projects
  const getUniqueOrganizations = (filteredProjects) => {
    const organizations = new Set(filteredProjects.map(project => project.organization));
    return Array.from(organizations);
  };

  // Add this new function to get unique projects for dropdown
  const getUniqueProjects = () => {
    const uniqueProjects = [];
    const seen = new Set();
    
    projects.forEach(project => {
      if (!seen.has(project.projectName)) {
        seen.add(project.projectName);
        uniqueProjects.push(project);
      }
    });
    
    return uniqueProjects;
  };

  // Filter projects based on selected project first
  const projectsFilteredByName = selectedProject
    ? projects.filter((project) => project.projectName === selectedProject)
    : projects;

  // Get available organizations for the filtered projects
  const availableOrganizations = getUniqueOrganizations(projectsFilteredByName);

  // Then filter by organization if selected
  const filteredProjects = activeOrganization
    ? projectsFilteredByName.filter((project) => project.organization === activeOrganization)
    : projectsFilteredByName;

  // Modify the button rendering to highlight active organization
  const renderOrganizationButton = (organization, label) => (
    <Button
      variant="contained"
      color={activeOrganization === organization ? "secondary" : "primary"}
      sx={{ 
        marginBottom: '10px',
        backgroundColor: activeOrganization === organization ? '#f50057' : undefined 
      }}
      onClick={() => handleButtonClick(organization)}
    >
      {label}
    </Button>
  );

  const handleRefresh = () => {
    setSelectedProject('');
    setActiveOrganization(null);
    const map = document.querySelector('.leaflet-container')?._leafletInstance;
    if (map) {
      map.setView([-3, 27], 4);
    }
  };

  // Modify the organization buttons section
  const renderOrganizationButtons = () => {
    if (!userData?.selectedOption === 'Comesa') return null;

    const organizations = ['ALL', 'COMESA', 'EAC', 'SADC', 'IOC', 'IGAD'];
    
    return (
      <Box sx={{ flexDirection: 'column', marginRight: '20px', minWidth: '120px', display: 'flex' }}>
        {selectedProject && (
          <Button
            variant="contained"
            color="warning"
            sx={{ marginBottom: '10px' }}
            onClick={handleRefresh}
          >
            Back
          </Button>
        )}
        {organizations.map(org => {
          // Show ALL button or only organizations that exist in filtered projects
          if (org === 'ALL' || availableOrganizations.includes(org)) {
            return renderOrganizationButton(org, org);
          }
          return null;
        })}
      </Box>
    );
  };

  const groupedProjects = groupProjectsByLocation(filteredProjects);

  return (
    <Container sx={{ 
      marginTop: { xs: '10px', sm: '20px' },
      marginBottom: { xs: '60px', sm: '80px' }, // Add bottom margin for footer
      height: { xs: 'calc(100vh - 180px)', sm: 'calc(100vh - 200px)' }, // Adjust height calculation
      padding: { xs: '0 10px', sm: '0 20px' },
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Box sx={{ mb: { xs: 1, sm: 2 } }}>
        <FormControl fullWidth>
          <Select
            value={selectedProject}
            onChange={handleProjectSelect}
            displayEmpty
            sx={{ 
              backgroundColor: 'white',
              fontSize: { xs: '14px', sm: '16px' }
            }}
          >
            <MenuItem value="">
              <em>Select a project</em>
            </MenuItem>
            {getUniqueProjects().map((project, index) => (
              <MenuItem key={index} value={project.projectName}>
                {project.projectName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        width: '100%',
        flex: 1,
        minHeight: 0, // Important for proper flex behavior
        gap: { xs: 1, sm: 2 } 
      }}>
        <Box sx={{ 
          flexDirection: { xs: 'row', md: 'column' },
          marginBottom: { xs: 1, md: 0 },
          minWidth: { xs: '100%', md: '120px' },
          display: 'flex',
          gap: 1,
          overflowX: { xs: 'auto', md: 'visible' },
          overflowY: { xs: 'hidden', md: 'auto' },
          padding: { xs: '5px', md: 0 },
          flexShrink: 0, // Prevent buttons from shrinking
        }}>
          {renderOrganizationButtons()}
        </Box>
        
        <Box sx={{ 
          flex: 1,
          minHeight: 0, // Important for proper flex behavior
          width: '100%',
          '& .leaflet-container': {
            height: '100% !important',
            width: '100%'
          }
        }}>
          <MapContainer 
            center={[-3, 27]} 
            zoom={4} 
            minZoom={3} 
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
            {Object.values(groupedProjects).map((group, index) => (
              <Marker
                key={index}
                position={[group.latitude, group.longitude]}
                icon={createCustomIcon(group)}
              >
                <Popup>
                  <Box sx={{
                    maxHeight: { xs: '200px', sm: '300px' },
                    overflowY: 'auto',
                    paddingRight: '10px',
                    '& p': {
                      fontSize: { xs: '12px', sm: '14px' },
                      margin: { xs: '4px 0', sm: '8px 0' }
                    },
                    '& strong': {
                      fontSize: { xs: '14px', sm: '18px' }
                    }
                  }}>
                    {group.projects.map((project, idx) => {
                      const { 
                        projectName, 
                        description, 
                        organization, 
                        location, 
                        startDate, 
                        endDate, 
                        displayRegions,
                        isMainLocation,
                        isCollaboratingState 
                      } = project;
                      
                      return (
                        <Box key={idx} sx={{ marginBottom: '10px' }}>
                          <Divider sx={{ marginY: 2 }} />
                          <strong style={{ fontSize: '18px' }}>{projectName}</strong>
                          {description && (
                            <p dangerouslySetInnerHTML={{ __html: description.replace(/\n/g, '<br/>') }} />
                          )}
                          {organization && (
                            <p>Organization: {organization}</p>
                          )}
                          {location && location.name && (
                            <p>Location: {location.name}</p>
                          )}
                          {displayRegions?.length > 0 && (
                            <p>{isMainLocation ? 'Primary Region' : 'Collaborating Regions'}: {displayRegions.join(', ')}</p>
                          )}
                          {formatDate(startDate) && (
                            <p>Start Date: {formatDate(startDate)}</p>
                          )}
                          {formatDate(endDate) && (
                            <p>End Date: {formatDate(endDate)}</p>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </Box>
      </Box>
    </Container>
  );
};

const AfricaMapsWrapper = () => {
  return (
    <ErrorBoundary>
      <AfricaMaps />
    </ErrorBoundary>
  );
};

export default AfricaMapsWrapper;
